/**
 * How a data field's values look in the customer drawer (CIN-7177 / JRAE-52).
 *
 * A field can carry an optional `display` object in company.fields. With none, the drawer shows
 * the value as plain text, as it always has. With `style: 'chip'`, values become tinted chips
 * (Task Manager's STAGE_COLORS keys), and `pin: true` also puts them under the customer's name:
 *
 *   { style: 'chip', pin: true, header_label: 'Spend',
 *     rules: [{ equals: '1', label: 'Top 20%', color: 'teal' }],    // text, list and yes/no
 *     fallback: { color: 'slate' } }                                 // "Any other value"
 *   number rules are ranges instead: { min: 0, max: 45, label: '{value} days', color: 'green' }
 *
 * WHY here and not in the page: the drawer renders it and the Data Fields page edits and previews
 * it, so both read one rule set, as with drawer-summary-fields.js. Kept free of imports so
 * scripts/data-fields-logic.test.js can load it under plain Node.
 */

// Types a chip rule can match. Dates show as dates; a "days since" chip comes from a number field.
export const CHIP_DISPLAY_TYPES = ['text', 'list', 'boolean', 'number'];

// The header holds this many chips; the rest collapse into "+N" with a tooltip. The row stays on
// one line (long chips truncate), so the name and tabs never get pushed around.
export const MAX_PINNED_CHIPS = 4;

// New rules take these in turn. Slate is left for the "Any other value" fallback.
export const RULE_COLOR_CYCLE = ['teal', 'blue', 'green', 'amber', 'orange', 'red', 'purple', 'pink'];
export const FALLBACK_COLOR = 'slate';
const isEmpty = value => value === undefined || value === null || typeof value === 'string' && value.trim() === '' || Array.isArray(value) && !value.length;
const toNumberOrNull = value => value === '' || value == null || !Number.isFinite(Number(value)) ? null : Number(value);

// A display can be written through the API rather than this page, so nothing in it is trusted:
// rules may be missing, not a list, or hold nulls, and labels may not be strings.
const rulesOf = display => Array.isArray(display?.rules) ? display.rules.filter(rule => rule && typeof rule === 'object') : [];
const labelOf = rule => String(rule.label ?? '').trim();

/**
 * A customer's raw value for a Customer data field, for chips. Field ids are paths with `__`
 * between keys (`entity_data__visits__last_store`), matched case-insensitively the way the Summary
 * tab reads entity_data, and a payload that arrives flattened (the whole id as one key) is read as is.
 *
 * WHY not the drawer's getter(): it formats for plain text, so objects and lists come back as JSON
 * strings. A nested list would then be one chip reading ["a","b"] instead of a chip per item.
 */
export function readCustomerValue(customer, fieldId) {
  if (!customer || typeof customer !== 'object' || !fieldId) return undefined;
  if (fieldId in customer) return customer[fieldId];
  let value = customer;
  for (const key of String(fieldId).split('__')) {
    if (value == null || typeof value !== 'object') return undefined;
    const match = Object.keys(value).find(candidate => candidate.toLowerCase() === key.toLowerCase());
    if (match === undefined) return undefined;
    value = value[match];
  }
  return value;
}

// Fields without a type, which a refresh adds, are text everywhere else too.
export const displayType = field => CHIP_DISPLAY_TYPES.includes(field?.type) ? field.type : field?.type ? null : 'text';
export const supportsChipDisplay = field => !!displayType(field);
export const isChipDisplay = field => field?.display?.style === 'chip' && supportsChipDisplay(field);

// Integrations send "ACTIVE", "active " and so on for the same thing, so matching ignores case and
// surrounding spaces. Exact only: no contains or patterns in v1.
export const matchKey = value => String(value).trim().toLowerCase();
const TRUE_WORDS = ['true', 'yes', 'y', '1', 't'];
const FALSE_WORDS = ['false', 'no', 'n', '0', 'f'];

// Yes/no fields arrive as booleans or as words, depending on the source.
export const booleanKey = value => {
  const key = matchKey(value);
  if (TRUE_WORDS.includes(key)) return 'true';
  if (FALSE_WORDS.includes(key)) return 'false';
  return key;
};
const fillTemplate = (label, text) => label.replace(/\{value\}/g, text);

/**
 * What a value reads as before any label: objects as JSON (as the drawer's plain formatting shows
 * them), numbers to at most two decimals (as the drawer shows its prediction scores), and yes/no as
 * Yes or No whatever word the source used.
 */
const displayText = (type, value) => {
  if (value !== null && typeof value === 'object') return JSON.stringify(value);
  if (type === 'number' && String(value).trim() !== '' && Number.isFinite(Number(value))) {
    const number = Number(value);
    return String(Number.isInteger(number) ? number : Number(number.toFixed(2)));
  }
  if (type === 'boolean') {
    const key = booleanKey(value);
    if (key === 'true') return 'Yes';
    if (key === 'false') return 'No';
  }
  return String(value).trim();
};
const findRule = (type, rules, value) => {
  if (type === 'number') {
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return rules.find(rule => {
      const min = toNumberOrNull(rule.min);
      const max = toNumberOrNull(rule.max);
      return (min == null || number >= min) && (max == null || number < max);
    }) || null;
  }
  const keyOf = type === 'boolean' ? booleanKey : matchKey;
  const key = keyOf(value);
  return rules.find(rule => rule.equals != null && keyOf(rule.equals) === key) || null;
};

/**
 * One value → { text, color } (color null = no matching rule and no fallback, so it reads as plain
 * text), or null when a rule hides it (yes/no fields can hide "false").
 */
function resolveOne(field, value) {
  const type = displayType(field);
  const {
    fallback
  } = field.display;
  const text = displayText(type, value);
  const rule = findRule(type, rulesOf(field.display), value);
  if (rule) {
    if (rule.hide) return null;
    const label = labelOf(rule);
    return {
      text: label ? fillTemplate(label, text) : text,
      color: rule.color || FALLBACK_COLOR
    };
  }
  return {
    text,
    color: fallback?.color || null
  };
}

/**
 * How a value renders. Returns null when the field has no chip display, so the caller keeps its
 * own formatting; otherwise a list of { text, color } (one per list item, possibly empty).
 */
export function resolveDisplay(field, value) {
  if (!isChipDisplay(field)) return null;
  if (isEmpty(value)) return [];
  const values = displayType(field) === 'list' && Array.isArray(value) ? value.filter(item => !isEmpty(item)) : [value];
  return values.map(item => resolveOne(field, item)).filter(Boolean);
}

/**
 * The chips under the customer's name: every pinned field in the Customer model's order. A value
 * with no matching rule still shows here (in the fallback color, or slate), since the header has
 * nothing else to show it with. `getValue(field)` reads the customer's value.
 */
export function pinnedChips(customerFields, getValue) {
  const chips = [];
  (Array.isArray(customerFields) ? customerFields : []).forEach(field => {
    if (!isChipDisplay(field) || !field.display.pin) return;
    const prefix = String(field.display.header_label ?? '').trim();
    resolveDisplay(field, getValue(field)).forEach((chip, index) => {
      chips.push({
        key: `${field.id}:${index}`,
        fieldName: field.name,
        text: prefix ? `${prefix} · ${chip.text}` : chip.text,
        color: chip.color || FALLBACK_COLOR
      });
    });
  });
  return {
    shown: chips.slice(0, MAX_PINNED_CHIPS),
    overflow: chips.slice(MAX_PINNED_CHIPS)
  };
}

// --- editing ------------------------------------------------------------------------------------

/** "Under 45", "45 to 90", "90 or more": what a number rule covers, in words. */
export function describeRange(rule) {
  // The Edit modal's From/To inputs hold '' while blank, which means no limit.
  const min = toNumberOrNull(rule.min);
  const max = toNumberOrNull(rule.max);
  if (min == null && max == null) return 'Any number';
  if (min == null) return `Under ${max}`;
  if (max == null) return `${min} or more`;
  return `${min} to ${max}`;
}

/**
 * A rule as a preview chip: { text, color, covers }. The text is what the drawer would show for a
 * value the rule matches (a number rule uses an example value from its range); `covers` says which
 * values it applies to, for a tooltip.
 */
export function describeRule(field, rule) {
  const label = labelOf(rule);
  const type = displayType(field);
  if (type === 'number') {
    const min = toNumberOrNull(rule.min);
    const max = toNumberOrNull(rule.max);
    const example = min != null ? min : max != null ? max - 1 : 0;
    return {
      text: fillTemplate(label || '{value}', displayText('number', example)),
      color: rule.color,
      covers: describeRange(rule)
    };
  }
  const equals = String(rule.equals ?? '').trim();
  return {
    text: label ? label : displayText(type, equals),
    color: rule.color,
    covers: equals
  };
}
export const nextRuleColor = (rules = []) => RULE_COLOR_CYCLE[rules.length % RULE_COLOR_CYCLE.length];

/** Rows to add for the most common sampled values that don't have a rule yet (text and lists). */
export function rulesFromSampleCounts(values = [], rules = [], limit = 10) {
  const taken = new Set(rules.map(rule => matchKey(rule.equals ?? '')));
  const added = [];
  // Indexed fields come back sorted A to Z, not by count, so sort here.
  const byCount = [...values].sort((a, b) => (b.count || 0) - (a.count || 0));
  byCount.forEach(({
    value
  }) => {
    if (added.length >= limit || isEmpty(value) || taken.has(matchKey(value))) return;
    taken.add(matchKey(value));
    added.push({
      equals: String(value).trim(),
      label: '',
      color: nextRuleColor([...rules, ...added])
    });
  });
  return added;
}

/** Three bands across a sampled number range, as a starting point people adjust. */
export function rulesFromSampleRange(min, max) {
  const low = Number(min);
  const high = Number(max);
  if (!Number.isFinite(low) || !Number.isFinite(high) || high <= low) return [];
  const span = high - low;
  // Whole-number cut points read best, but a narrow range (scores between 0 and 1) needs decimals,
  // or every value would land in one band.
  const round = value => span >= 3 ? Math.round(value) : Number(value.toFixed(2));
  const cut1 = round(low + span / 3);
  const cut2 = round(low + span * 2 / 3);
  if (!(cut1 < cut2)) return [];
  return [{
    min: null,
    max: cut1,
    label: '',
    color: 'green'
  }, {
    min: cut1,
    max: cut2,
    label: '',
    color: 'amber'
  }, {
    min: cut2,
    max: null,
    label: '',
    color: 'red'
  }];
}

/**
 * "Fill from sample data": { rules, note }. Text and list fields gain a row per common value they
 * don't have yet. Number fields are split into three ranges. A custom (entity_data) number is
 * sampled as value counts rather than a min/max, because the sampler can't tell its type, so the
 * range comes from the numeric values in the counts.
 */
export function rulesFromSample(field, sample, rules = []) {
  if (!sample || sample.state !== 'ready') {
    return {
      rules,
      note: 'No sample data for this field yet. Refresh data fields, or add values by hand.'
    };
  }
  if (displayType(field) === 'number') {
    const numbers = sample.kind === 'range' ? [Number(sample.min), Number(sample.max)] : (sample.values || []).map(({
      value
    }) => Number(value)).filter(value => Number.isFinite(value));
    const low = Math.min(...numbers);
    const high = Math.max(...numbers);
    const bands = numbers.length ? rulesFromSampleRange(low, high) : [];
    if (!bands.length) return {
      rules,
      note: 'The sample has no spread of numbers to split. Add ranges by hand.'
    };
    return {
      rules: bands,
      note: `Split ${low} to ${high} into three ranges. Adjust them to fit.`
    };
  }
  if (sample.kind !== 'counts') return {
    rules,
    note: 'No sample values to add. Add values by hand.'
  };
  const added = rulesFromSampleCounts(sample.values, rules);
  return {
    rules: [...rules, ...added],
    note: added.length ? null : 'Every common value already has a rule.'
  };
}

/**
 * What the Edit modal saves: null for plain text (the key is removed, so the field is exactly as it
 * was before display settings existed), otherwise a trimmed object without blank rows.
 */
export function cleanDisplay(display, field) {
  if (!display || display.style !== 'chip' || !supportsChipDisplay(field)) return null;
  const isNumber = displayType(field) === 'number';
  // Each rule is rebuilt key by key, so the editor's `_key` never reaches company.fields.
  const rules = rulesOf(display).map(rule => isNumber ? {
    min: toNumberOrNull(rule.min),
    max: toNumberOrNull(rule.max),
    label: labelOf(rule),
    color: rule.color
  } : {
    equals: String(rule.equals ?? '').trim(),
    label: labelOf(rule),
    color: rule.color,
    ...(rule.hide ? {
      hide: true
    } : {})
  }).filter(rule => isNumber ? true : rule.equals !== '').map(rule => {
    const next = {
      ...rule
    };
    if (!next.label) delete next.label;
    return next;
  });
  const cleaned = {
    style: 'chip',
    rules
  };
  if (display.fallback?.color) cleaned.fallback = {
    color: display.fallback.color
  };
  if (display.pin) {
    cleaned.pin = true;
    const headerLabel = String(display.header_label ?? '').trim();
    if (headerLabel) cleaned.header_label = headerLabel;
  }
  return cleaned;
}

// React keys for the editor's rule rows. With index keys, removing a row hands its neighbour's
// open color picker to the wrong rule.
let lastRuleKey = 0;
export const withRuleKeys = rules => rules.map(rule => rule._key ? rule : {
  ...rule,
  _key: `rule-${lastRuleKey += 1}`
});

/** What the Edit modal's editor works on, from the stored field: a full draft even when nothing's stored yet. */
export function toDisplayDraft(field) {
  const stored = field.display || {};
  let rules = rulesOf(stored);
  // Yes/no always has exactly these two rows, so people style both answers in place.
  if (displayType(field) === 'boolean') {
    const find = key => rules.find(rule => String(rule.equals) === key);
    rules = [{
      equals: 'true',
      label: '',
      color: 'green',
      ...find('true')
    }, {
      equals: 'false',
      label: '',
      color: 'slate',
      ...find('false')
    }];
  }
  return {
    style: stored.style === 'chip' ? 'chip' : 'plain',
    rules: withRuleKeys(rules),
    fallback: stored.fallback?.color ? {
      color: stored.fallback.color
    } : null,
    pin: !!stored.pin,
    header_label: String(stored.header_label ?? '')
  };
}

/** A number rule whose From isn't below its To can never match. */
export const isInvalidRange = rule => {
  const min = toNumberOrNull(rule.min);
  const max = toNumberOrNull(rule.max);
  return min != null && max != null && min >= max;
};

/** The modal won't save a chip display with a range that can never match. */
export const hasInvalidRange = (draft, field) => draft.style === 'chip' && displayType(field) === 'number' && draft.rules.some(isInvalidRange);